/**
 * timezones.ts — de tijdzone van een project.
 *
 * Twee dingen die makkelijk door elkaar lopen:
 *
 *  - Een TIJDSTEMPEL ('aangemaakt op') is een moment. Dat bewaren we in UTC en tonen we in de
 *    tijdzone van de kijker; daar komt dit bestand niet aan te pas (zie utils/datetime).
 *  - Een DATUM ('de boring van 24 september') is een kalenderdag, en die is alleen betekenisvol
 *    ergens. Hij hoort de dag te zijn waarop er geboord is, op de plek waar geboord werd -- niet
 *    de dag waar de lezer toevallig zit, want dan verspringt de datum in een rapport zodra een
 *    collega het elders opent.
 *
 * Daarom hangt de tijdzone aan het PROJECT en niet aan de gebruiker.
 */

/**
 * De uitleg bij het veld, in de backoffice en de veldapp dezelfde.
 *
 * Hij benoemt allebei de helften van het onderscheid hierboven, want de verwarring zit er juist
 * tussenin: mensen lezen 'tijdzone' en denken dat ze de klok van de applicatie omzetten.
 */
export const TIJDZONE_UITLEG =
  'De project tijdzone bepaalt in welke tijdzone de datum van een boring, monster of peilbuis '
  + 'valt. Exacte tijdstippen worden weergegeven in de tijdzone waar je zelf bent tijdens het '
  + 'gebruik van de app.';

/** Nederlandse projecten zijn de regel; wie elders werkt zet hem om. */
export const DEFAULT_TIMEZONE = 'Europe/Amsterdam';

/**
 * Een korte lijst voor omgevingen zonder `Intl.supportedValuesOf` (de veldapp draait op Hermes).
 * Geen wereldomvattende lijst: de zones waar Nederlandse bodemonderzoekers werken, plus UTC.
 */
const TERUGVAL_ZONES = [
  'UTC',
  'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Berlin', 'Europe/Paris', 'Europe/London',
  'Europe/Madrid', 'Europe/Lisbon', 'Europe/Rome', 'Europe/Warsaw', 'Europe/Oslo',
  'Europe/Stockholm', 'Europe/Copenhagen', 'Europe/Helsinki', 'Europe/Dublin', 'Europe/Zurich',
  'Europe/Vienna', 'Europe/Prague', 'Europe/Budapest', 'Europe/Bucharest', 'Europe/Athens',
  'Europe/Kyiv', 'Europe/Istanbul', 'Europe/Moscow',
  'Atlantic/Reykjavik', 'Atlantic/Canary',
  'America/St_Johns', 'America/Halifax', 'America/Toronto', 'America/New_York',
  'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Vancouver',
  'America/Mexico_City', 'America/Bogota', 'America/Lima', 'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires', 'America/Paramaribo', 'America/Curacao',
  'Africa/Casablanca', 'Africa/Lagos', 'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Nairobi',
  'Asia/Dubai', 'Asia/Karachi', 'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Shanghai',
  'Asia/Hong_Kong', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Seoul', 'Asia/Jakarta',
  'Australia/Perth', 'Australia/Adelaide', 'Australia/Sydney', 'Pacific/Auckland',
];

/** Kent deze omgeving die tijdzone? Een onbekende naam laat Intl afgaan met een RangeError. */
export function isValidTimeZone(tz: unknown): boolean {
  if (typeof tz !== 'string' || !tz.trim()) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Een tijdzone of de standaard, zodat een lege of onzinnige waarde nooit doorwerkt. */
export function normalizeTimeZone(tz: unknown): string {
  // Eerst de spaties eraf: Intl kent 'America/Toronto' wel en ' America/Toronto ' niet, en een
  // waarde die uit een formulier of een import komt draagt ze zomaar.
  const schoon = typeof tz === 'string' ? tz.trim() : tz;
  return isValidTimeZone(schoon) ? String(schoon) : DEFAULT_TIMEZONE;
}

/**
 * De huidige afstand tot UTC, als 'UTC+02:00'.
 *
 * Bewust nú berekend en niet als vaste tekst in een lijst: half Europa schuift twee keer per
 * jaar een uur op, en 'Europe/Amsterdam (UTC+01:00)' in juli is gewoon onwaar.
 */
export function timeZoneOffsetLabel(tz: string | null | undefined, at: Date = new Date()): string {
  try {
    const delen = new Intl.DateTimeFormat('en-US', { timeZone: tz || undefined, timeZoneName: 'longOffset' })
      .formatToParts(at);
    const naam = delen.find((d) => d.type === 'timeZoneName')?.value || '';
    // 'GMT+02:00' → 'UTC+02:00'; op GMT zelf geeft Intl kaal 'GMT'.
    if (naam === 'GMT' || naam === 'UTC') return 'UTC+00:00';
    return naam.replace(/^GMT/, 'UTC');
  } catch {
    return '';
  }
}

/** Alle tijdzones die deze omgeving kent, met de standaard vooraan. */
export function timeZoneOptions(): Array<{ value: string; label: string }> {
  let zones: string[];
  try {
    const ietf = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
    zones = typeof ietf.supportedValuesOf === 'function' ? ietf.supportedValuesOf('timeZone') : [];
  } catch {
    zones = [];
  }
  if (!zones.length) zones = [...TERUGVAL_ZONES];
  if (!zones.includes('UTC')) zones.unshift('UTC');
  if (!zones.includes(DEFAULT_TIMEZONE)) zones.unshift(DEFAULT_TIMEZONE);
  // De standaard bovenaan: die kiest verreweg de meeste projecten.
  const rest = zones.filter((z) => z !== DEFAULT_TIMEZONE).sort();
  return [DEFAULT_TIMEZONE, ...rest].map((value) => ({
    value,
    label: `${value.replace(/_/g, ' ')} (${timeZoneOffsetLabel(value)})`,
  }));
}

/**
 * De kalenderdag in een tijdzone, als 'JJJJ-MM-DD'.
 *
 * De 'sv-SE'-locale levert precies dat formaat; dat is geen taalkeuze maar de kortste weg naar
 * ISO zonder zelf aan datumrekenen te slaan.
 */
export function todayInZone(tz: string | null | undefined, at: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('sv-SE', { timeZone: normalizeTimeZone(tz) }).format(at);
  } catch {
    return at.toISOString().slice(0, 10);
  }
}

/** Datum ('JJJJ-MM-DD') en tijd ('UU:MM') in een tijdzone. */
export function nowInZone(tz: string | null | undefined, at: Date = new Date()): { datum: string; tijd: string } {
  const zone = normalizeTimeZone(tz);
  try {
    const p = Object.fromEntries(new Intl.DateTimeFormat('sv-SE', {
      timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(at).map((x) => [x.type, x.value]));
    return { datum: `${p.year}-${p.month}-${p.day}`, tijd: `${p.hour}:${p.minute}` };
  } catch {
    const iso = at.toISOString();
    return { datum: iso.slice(0, 10), tijd: iso.slice(11, 16) };
  }
}
